import {computed} from "mobx";
import {AccountStore} from "../../Account/AccountStore";
import {AppStore} from "../../Core/AppStore";
import {ColDef, ColGroupDef, GridOptions} from "ag-grid-enterprise";
import {SelectAllHeader} from "./GridFields/DisplayFields";
import {ActiveDisplay} from "../ActiveDisplay";
import {getDisplayStatusFields} from "./GridFields/DisplayFields_Status";
import {getDisplayTelemetryFields} from "./GridFields/DisplayFields_Telemetry";
import {getDisplayConfigFields} from "./GridFields/DisplayFields_Config";

export class GridConfig {
    constructor(
        private accountStore: AccountStore,
        private appStore: AppStore,
    ) {}

    @computed
    public get gridOptions(): GridOptions<ActiveDisplay> {
        return {
            rowSelection: this.appStore.storage.showBulkActionsMenu ? {
                mode: "multiRow",
                selectAll: "all",
                checkboxLocation: "selectionColumn",
            } : undefined,
            selectionColumnDef: {
                sortable: false,
                resizable: true,
                suppressHeaderMenuButton: false,
                headerTooltip: 'Checkboxes indicate selection',
                pinned: 'left',
                headerComponent: SelectAllHeader
            },
            headerHeight: this.appStore.gridAutoHeaderHeight ? undefined : this.appStore.gridHeaderHeight,
            groupHeaderHeight: this.accountStore.storage.gridGroupHeaderHeight,
            sideBar: {
                position: "right",
                toolPanels: [
                    {
                        id: "columns",
                        labelDefault: "Columns",
                        labelKey: "columns",
                        iconKey: "columns",
                        toolPanel: "agColumnsToolPanel",
                        width: 350,
                        toolPanelParams: {
                            suppressRowGroups: true,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            defaultGroupExpanded: true // this ensures groups are expanded
                        },
                    },
                ]
            },
            processUnpinnedColumns: (params) => {
                const gridElement = document.querySelector('.ag-root') as HTMLElement;
                const gridWidth = gridElement?.clientWidth ?? 0;
                const maxLeftPinnedWidth = gridWidth * this.accountStore.storage.gridLeftPinnedContainerWidth;
                const maxRightPinnedWidth = gridWidth * this.accountStore.storage.gridRightPinnedContainerWidth;

                let leftPinnedWidth = 0;
                let rightPinnedWidth = 0;
                const columnsToUnpin: any[] = [];

                for (const col of params.columns) {
                    const colWidth = col.getActualWidth();
                    const pinned = col.getPinned();

                    if (pinned === 'left') {
                        leftPinnedWidth += colWidth;
                        if (
                            leftPinnedWidth > maxLeftPinnedWidth &&
                            !this.accountStore.storage.gridLeftPinnedContainerScrollable
                        ) {
                            columnsToUnpin.push(col);
                        }
                    } else if (pinned === 'right') {
                        rightPinnedWidth += colWidth;
                        if (
                            rightPinnedWidth > maxRightPinnedWidth &&
                            !this.accountStore.storage.gridRightPinnedContainerScrollable
                        ) {
                            columnsToUnpin.push(col);
                        }
                    }
                }

                return columnsToUnpin;
            }

        }
    }

    @computed
    public get defaultColDef(): ColDef {
        return {
            cellClass: this.accountStore.storage.gridCenterAlignColumns ? 'ag-center-cols-cell' : '',
            headerClass: this.accountStore.storage.gridCenterAlignHeaders ? 'ag-center-header' : '',
            cellStyle: { padding: `${this.accountStore.storage.gridColumnPadding}px` },
            enableCellChangeFlash: true,
            wrapHeaderText: this.accountStore.storage.gridWrapText,
            autoHeaderHeight: this.appStore.gridAutoHeaderHeight,
            wrapText: this.accountStore.storage.gridWrapText,
            suppressHeaderMenuButton: true
        };
    }

    @computed
    public get gridTheme() {
        let theme = this.accountStore.storage.gridTheme;
        const bold = this.accountStore.storage.gridBoldText ? ' grid-bold ' : '';
        let className = ' minimal-padding ' + bold;
        if (!theme) theme = 'ag-theme-balham';
        className = className + theme;

        return className + ' ' + this.accountStore.storage.gridThemeSize;
    }

    @computed
    public get isImageColumnSelected() {
        let isPopVu1Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot1");
        let isPopVu2Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot2");
        let isPopVu3Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot3");
        let isPopVu4Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot4");
        let isSelected = false;
        if (isPopVu1Selected && isPopVu1Selected.isVisible()) isSelected = true;
        if (isPopVu2Selected && isPopVu2Selected.isVisible()) isSelected = true;
        if (isPopVu3Selected && isPopVu3Selected.isVisible()) isSelected = true;
        if (isPopVu4Selected && isPopVu4Selected.isVisible()) isSelected = true;

        return isSelected;
    }

    // COLUMN DEFS
    @computed
    public get colDefs(): ColGroupDef<ActiveDisplay>[] {
        return [
            ...getDisplayStatusFields(this.accountStore),
            ...getDisplayTelemetryFields(this.accountStore),
            ...getDisplayConfigFields(this.accountStore)
        ];

    }
}
